import React, { useState, useEffect } from 'react';
import { Plus, UserCheck, UserX, Eye, GraduationCap } from 'lucide-react';
import { toast } from 'react-toastify';
import StudentAdmissionForm from '../components/students/StudentAdmissionForm';
import AdmissionDetailsView from '../components/students/AdmissionDetailsView';
import { ConfirmDialog, InputDialog } from '../components/ui';
import { studentsApi, registrationApi, schoolDataApi } from '../services/api';
import { Student, StudentAdmissionFormData, StudentRegistration, SchoolClass } from '../types';
import { formatDate } from '../utils/helpers';
import { sortClasses } from '../utils/classUtils';

const Admissions: React.FC = () => {
  const [students, setStudents] = useState<Student[]>([]);
  const [registrations, setRegistrations] = useState<StudentRegistration[]>([]);
  const [classes, setClasses] = useState<SchoolClass[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [selectedRegistration, setSelectedRegistration] = useState<StudentRegistration | null>(null);
  const [viewingStudent, setViewingStudent] = useState<Student | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Dialog states
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
    type?: 'danger' | 'warning' | 'info';
    confirmText?: string;
    cancelText?: string;
  }>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {}
  });

  const [inputDialog, setInputDialog] = useState<{
    isOpen: boolean;
    title: string;
    message?: string;
    placeholder?: string;
    defaultValue?: string;
    options?: string[];
    onSubmit: (value: string) => void;
  }>({
    isOpen: false,
    title: '',
    onSubmit: () => {}
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [studentsData, registrationsData, classesData] = await Promise.all([
        studentsApi.getAll(),
        registrationApi.getAll(),
        schoolDataApi.classes.getAll()
      ]);
      setStudents(studentsData);
      setRegistrations(registrationsData);
      setClasses(sortClasses(classesData));
    } catch (error) {
      toast.error('Failed to load data');
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAdmissionForm = (registration: StudentRegistration) => {
    setSelectedRegistration(registration);
    setShowForm(true);
  };

  const handleSubmitAdmissionForm = async (data: StudentAdmissionFormData) => {
    if (!selectedRegistration) return;

    try {
      setSubmitting(true);
      const newStudent = await studentsApi.createFromRegistration(
        selectedRegistration.registrationId,
        selectedRegistration,
        data
      );
      setStudents(prev => [newStudent, ...prev]);
      setShowForm(false);
      setSelectedRegistration(null);
      toast.success('Admission form submitted successfully');
    } catch (error) {
      toast.error('Failed to submit admission form');
      console.error('Error submitting admission form:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleApproveAdmission = (student: Student) => {
    setInputDialog({
      isOpen: true,
      title: 'Approve Admission',
      message: `Select final class assignment for ${student.name}:`,
      placeholder: 'Select class',
      defaultValue: student.class,
      options: classes.map(c => c.name),
      onSubmit: (classAssigned) => {
        setInputDialog(prev => ({ ...prev, isOpen: false }));

        const proceedToConfirm = (section?: string) => {
          setConfirmDialog({
            isOpen: true,
            title: 'Approve Admission',
            message: `Are you sure you want to approve admission for ${student.name} in ${classAssigned}${section ? ` - Section ${section}` : ''}?`,
            type: 'info',
            confirmText: 'Yes, Approve',
            cancelText: 'Cancel',
            onConfirm: async () => {
              try {
                await studentsApi.approveAdmission(student.id, classAssigned, section);
                setStudents(prev =>
                  prev.map(s =>
                    s.id === student.id
                      ? {
                          ...s,
                          admissionStatus: 'Approved' as const,
                          class: classAssigned,
                          ...(section ? { section } : {}),
                          admissionDate: new Date().toISOString(),
                        }
                      : s
                  )
                );
                toast.success('Admission approved successfully');
              } catch (error) {
                toast.error('Failed to approve admission');
                console.error('Error approving admission:', error);
              }
            }
          });
        };

        const matchedClass = classes.find(c => c.name === classAssigned);
        const availableSections = matchedClass?.sections || [];

        if (availableSections.length > 0) {
          // InputDialog calls onClose() right after onSubmit(), which would
          // otherwise immediately close the section dialog we're about to open.
          // Deferring lets that close-then-reopen settle in the right order.
          setTimeout(() => {
            setInputDialog({
              isOpen: true,
              title: 'Approve Admission',
              message: `Select section for ${student.name} in ${classAssigned}:`,
              placeholder: 'Select section',
              defaultValue: student.section || '',
              options: availableSections,
              onSubmit: (section) => {
                setInputDialog(prev => ({ ...prev, isOpen: false }));
                proceedToConfirm(section || undefined);
              }
            });
          }, 0);
        } else {
          proceedToConfirm();
        }
      }
    });
  };

  const handleRejectAdmission = async (student: Student) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Reject Admission',
      message: `Are you sure you want to reject admission for ${student.name}?\n\nThis action cannot be undone.`,
      type: 'danger',
      confirmText: 'Yes, Reject',
      cancelText: 'Cancel',
      onConfirm: async () => {
        try {
          await studentsApi.rejectAdmission(student.id);
          setStudents(prev =>
            prev.map(s =>
              s.id === student.id
                ? { ...s, admissionStatus: 'Rejected' as const }
                : s
            )
          );
          toast.success('Admission rejected');
        } catch (error) {
          toast.error('Failed to reject admission');
          console.error('Error rejecting admission:', error);
        }
      }
    });
  };

  const handleViewStudent = (student: Student) => {
    setViewingStudent(student);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Form_Pending':
        return 'bg-[#332711] text-[#f9daa3]';
      case 'Submitted':
        return 'bg-[#16212b] text-[#bfdbfe]';
      case 'Approved':
        return 'bg-[#173322] text-[#8ff0b8]';
      case 'Rejected':
        return 'bg-[#341a17] text-[#fbccc8]';
      default:
        return 'bg-[#b9ad9b] text-[#4a4235]';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'Form_Pending':
        return 'Admission Form Pending';
      case 'Submitted':
        return 'Under Review';
      case 'Approved':
        return 'Admitted';
      case 'Rejected':
        return 'Rejected';
      default:
        return status;
    }
  };

  // Get all student registration IDs to filter out completed admission forms
  const studentRegistrationIds = students.map(s => s.registrationId);

  // Approved registrations that haven't had admission forms submitted yet
  const approvedRegistrations = registrations.filter(r =>
    r.registrationStatus === 'Approved' &&
    !studentRegistrationIds.includes(r.registrationId)
  );

  // Pending admission applications (submitted but not approved/rejected)
  const pendingAdmissionForms = students.filter(s =>
    s.admissionStatus === 'Submitted' || s.admissionStatus === 'Form_Pending'
  );

  // Approved admissions - these can be kept for record purposes
  const approvedAdmissions = students.filter(s => s.admissionStatus === 'Approved');

  if (viewingStudent) {
    return (
      <AdmissionDetailsView
        student={viewingStudent}
        onBack={() => setViewingStudent(null)}
      />
    );
  }

  if (showForm && selectedRegistration) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-[#f3ede2]">Admission Form</h1>
            <p className="text-[#b9ad9b] mt-1">Complete admission for {selectedRegistration.name}</p>
          </div>
        </div>

        <StudentAdmissionForm
          onSubmit={handleSubmitAdmissionForm}
          loading={submitting}
          registrationId={selectedRegistration?.registrationId}
          registrationData={selectedRegistration}
          onCancel={() => {
            setShowForm(false);
            setSelectedRegistration(null);
          }}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#f3ede2]">Student Admissions</h1>
          <p className="text-[#b9ad9b] mt-1">Manage student admission process</p>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-[#211c15] rounded-lg border border-[#3a3122] p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[#b9ad9b] text-sm font-medium">Pending Admission Forms</p>
              <p className="text-2xl font-bold text-[#f3ede2] mt-2">{approvedRegistrations.length}</p>
            </div>
            <div className="w-12 h-12 bg-[#332711] rounded-lg flex items-center justify-center">
              <GraduationCap className="w-6 h-6 text-[#f0b64c]" />
            </div>
          </div>
        </div>

        <div className="bg-[#211c15] rounded-lg border border-[#3a3122] p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[#b9ad9b] text-sm font-medium">Under Review</p>
              <p className="text-2xl font-bold text-[#f3ede2] mt-2">{pendingAdmissionForms.length}</p>
            </div>
            <div className="w-12 h-12 bg-[#16212b] rounded-lg flex items-center justify-center">
              <Eye className="w-6 h-6 text-[#60a5fa]" />
            </div>
          </div>
        </div>

        <div className="bg-[#211c15] rounded-lg border border-[#3a3122] p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[#b9ad9b] text-sm font-medium">Admitted Students</p>
              <p className="text-2xl font-bold text-[#f3ede2] mt-2">{approvedAdmissions.length}</p>
            </div>
            <div className="w-12 h-12 bg-[#173322] rounded-lg flex items-center justify-center">
              <UserCheck className="w-6 h-6 text-[#4ade80]" />
            </div>
          </div>
        </div>

        <div className="bg-[#211c15] rounded-lg border border-[#3a3122] p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[#b9ad9b] text-sm font-medium">Total Applications</p>
              <p className="text-2xl font-bold text-[#f3ede2] mt-2">{students.length}</p>
            </div>
            <div className="w-12 h-12 bg-[#b9ad9b] rounded-lg flex items-center justify-center">
              <Plus className="w-6 h-6 text-[#5c5346]" />
            </div>
          </div>
        </div>
      </div>

      {/* Approved Registrations Awaiting Admission Form */}
      {approvedRegistrations.length > 0 && (
        <div className="bg-[#211c15] rounded-xl border border-[#3a3122]">
          <div className="p-6 border-b border-[#3a3122]">
            <h2 className="text-lg font-semibold text-[#f3ede2]">Approved Registrations - Awaiting Admission Form</h2>
            <p className="text-[#b9ad9b] text-sm mt-1">Students who have completed registration and are ready for admission form</p>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-[#211c15]">
                <tr>
                  <th className="text-left py-3 px-6 font-medium text-[#b9ad9b]">Student Info</th>
                  <th className="text-left py-3 px-6 font-medium text-[#b9ad9b]">Parent Info</th>
                  <th className="text-left py-3 px-6 font-medium text-[#b9ad9b]">Preferred Class</th>
                  <th className="text-left py-3 px-6 font-medium text-[#b9ad9b]">Approved Date</th>
                  <th className="text-left py-3 px-6 font-medium text-[#b9ad9b]">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#3a3122]">
                {approvedRegistrations.map((registration) => (
                  <tr key={registration.id} className="hover:bg-[#28221a]">
                    <td className="py-4 px-6">
                      <div>
                        <div className="font-medium text-[#f3ede2]">{registration.name}</div>
                        <div className="text-sm text-[#b9ad9b]">{registration.registrationId}</div>
                        <div className="text-sm text-[#b9ad9b]">{registration.gender}</div>
                      </div>
                    </td>
                    <td className="py-4 px-6">
                      <div>
                        <div className="font-medium text-[#f3ede2]">{registration.parentName}</div>
                        <div className="text-sm text-[#b9ad9b]">{registration.parentContact}</div>
                        <div className="text-sm text-[#b9ad9b]">{registration.parentEmail}</div>
                      </div>
                    </td>
                    <td className="py-4 px-6">
                      <span className="px-2 py-1 bg-[#16212b] text-[#bfdbfe] rounded-full text-sm">
                        {registration.preferredClass}
                      </span>
                    </td>
                    <td className="py-4 px-6 text-[#b9ad9b] text-sm">
                      {registration.approvedAt ? formatDate(registration.approvedAt) : '-'}
                    </td>
                    <td className="py-4 px-6">
                      <button
                        onClick={() => handleCreateAdmissionForm(registration)}
                        className="px-3 py-1 bg-[#e2916d] hover:bg-[#f0a67d] text-white rounded-lg transition-colors text-sm"
                      >
                        Fill Admission Form
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Admission Applications */}
      <div className="bg-[#211c15] rounded-xl border border-[#3a3122]">
        <div className="p-6 border-b border-[#3a3122]">
          <h2 className="text-lg font-semibold text-[#f3ede2]">Admission Applications</h2>
          <p className="text-[#b9ad9b] text-sm mt-1">Students who have submitted their admission forms</p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center p-12">
            <div className="w-8 h-8 border-4 border-[#e2916d] border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : pendingAdmissionForms.length === 0 ? (
          <div className="text-center py-12">
            <GraduationCap className="w-12 h-12 text-[#8a7f6e] mx-auto mb-4" />
            <p className="text-[#b9ad9b] text-lg">No admission applications found</p>
            <p className="text-[#8a7f6e] text-sm">Admission applications will appear here</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-[#211c15]">
                <tr>
                  <th className="text-left py-3 px-6 font-medium text-[#b9ad9b]">Student Info</th>
                  <th className="text-left py-3 px-6 font-medium text-[#b9ad9b]">Parent Info</th>
                  <th className="text-left py-3 px-6 font-medium text-[#b9ad9b]">Class</th>
                  <th className="text-left py-3 px-6 font-medium text-[#b9ad9b]">Status</th>
                  <th className="text-left py-3 px-6 font-medium text-[#b9ad9b]">Submitted</th>
                  <th className="text-left py-3 px-6 font-medium text-[#b9ad9b]">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#3a3122]">
                {pendingAdmissionForms.map((student) => (
                  <tr key={student.id} className="hover:bg-[#28221a]">
                    <td className="py-4 px-6">
                      <div>
                        <div className="font-medium text-[#f3ede2]">{student.name}</div>
                        <div className="text-sm text-[#b9ad9b]">{student.studentId}</div>
                        <div className="text-sm text-[#b9ad9b]">{student.gender}</div>
                      </div>
                    </td>
                    <td className="py-4 px-6">
                      <div>
                        <div className="font-medium text-[#f3ede2]">{student.parentName}</div>
                        <div className="text-sm text-[#b9ad9b]">{student.parentContact}</div>
                        <div className="text-sm text-[#b9ad9b]">{student.parentEmail}</div>
                      </div>
                    </td>
                    <td className="py-4 px-6">
                      <span className="px-2 py-1 bg-[#16212b] text-[#bfdbfe] rounded-full text-sm">
                        {student.class} {student.section && `- ${student.section}`}
                      </span>
                    </td>
                    <td className="py-4 px-6">
                      <span className={`px-2 py-1 rounded-full text-sm ${getStatusColor(student.admissionStatus)}`}>
                        {getStatusLabel(student.admissionStatus)}
                      </span>
                    </td>
                    <td className="py-4 px-6 text-[#b9ad9b] text-sm">
                      {formatDate(student.createdAt)}
                    </td>
                    <td className="py-4 px-6">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleViewStudent(student)}
                          className="p-2 text-[#8a7f6e] hover:text-[#60a5fa] hover:bg-[#16212b] rounded-lg transition-colors"
                          title="View Details"
                        >
                          <Eye className="w-4 h-4" />
                        </button>

                        {student.admissionStatus === 'Submitted' && (
                          <>
                            <button
                              onClick={() => handleApproveAdmission(student)}
                              className="p-2 text-[#8a7f6e] hover:text-[#4ade80] hover:bg-[#173322] rounded-lg transition-colors"
                              title="Approve Admission"
                            >
                              <UserCheck className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleRejectAdmission(student)}
                              className="p-2 text-[#8a7f6e] hover:text-[#f38b83] hover:bg-[#341a17] rounded-lg transition-colors"
                              title="Reject Admission"
                            >
                              <UserX className="w-4 h-4" />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Dialog Components */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => setConfirmDialog({ ...confirmDialog, isOpen: false })}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        message={confirmDialog.message}
        type={confirmDialog.type}
        confirmText={confirmDialog.confirmText}
        cancelText={confirmDialog.cancelText}
      />

      <InputDialog
        isOpen={inputDialog.isOpen}
        onClose={() => setInputDialog({ ...inputDialog, isOpen: false })}
        onSubmit={inputDialog.onSubmit}
        title={inputDialog.title}
        message={inputDialog.message}
        placeholder={inputDialog.placeholder}
        defaultValue={inputDialog.defaultValue}
        options={inputDialog.options}
      />
    </div>
  );
};

export default Admissions;